import { useState, useEffect } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, Palette, Tag } from "@phosphor-icons/react";

const PRESET_COLORS = [
  "#E07A5F", "#3D405B", "#81B29A", "#F2CC8F", "#9A8C98", 
  "#C9ADA7", "#264653", "#E9C46A", "#2E5C55", "#D97757"
];

export default function CategoriesPage() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({ name: "", color: PRESET_COLORS[0] });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await api.get("/categories");
      setCategories(response.data);
    } catch (error) {
      toast.error("Erro ao carregar categorias");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: "", color: PRESET_COLORS[0] });
    setEditingCategory(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await api.put(`/categories/${editingCategory.id}`, formData);
        toast.success("Categoria atualizada!");
      } else {
        await api.post("/categories", formData);
        toast.success("Categoria criada!");
      }
      setDialogOpen(false);
      resetForm();
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao salvar categoria");
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({ name: category.name, color: category.color });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Excluir categoria? As contas vinculadas ficarão sem categoria.")) return;
    try {
      await api.delete(`/categories/${id}`);
      toast.success("Categoria excluída");
      fetchCategories();
    } catch (error) {
      toast.error("Erro ao excluir categoria");
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[1, 2, 3].map(i => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-6">
              <div className="h-20 bg-muted rounded" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="categories-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">Categorias</h1>
          <p className="text-muted-foreground mt-1">
            Organize suas contas por tipo
          </p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="tracing-beam gap-2" data-testid="new-category-button">
              <Plus size={18} weight="bold" />
              Nova Categoria
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editingCategory ? "Editar Categoria" : "Nova Categoria"}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label>Nome da Categoria</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: Conta Pessoal, Conta Sonia..."
                  required
                  data-testid="category-name-input"
                />
              </div>

              <div className="space-y-3">
                <Label>Cor</Label>
                <div className="flex flex-wrap gap-2">
                  {PRESET_COLORS.map(color => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setFormData({ ...formData, color })}
                      className={`w-10 h-10 rounded-lg transition-all ${
                        formData.color === color 
                          ? 'ring-2 ring-offset-2 ring-primary scale-110' 
                          : 'hover:scale-105'
                      }`}
                      style={{ backgroundColor: color }}
                      data-testid={`color-${color}`}
                    />
                  ))}
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <Label className="text-sm text-muted-foreground">Cor personalizada:</Label>
                  <Input
                    type="color"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="w-12 h-10 p-1 cursor-pointer"
                    data-testid="color-picker"
                  />
                  <span className="text-sm font-mono text-muted-foreground">{formData.color}</span>
                </div>
              </div>

              {/* Preview */}
              <div className="p-4 rounded-lg border bg-muted/30">
                <Label className="text-sm text-muted-foreground mb-2 block">Preview</Label>
                <div className="flex items-center gap-3">
                  <div 
                    className="w-4 h-12 rounded-full"
                    style={{ backgroundColor: formData.color }}
                  />
                  <div>
                    <p className="font-medium">{formData.name || "Nome da Categoria"}</p>
                    <p className="text-sm text-muted-foreground">3 contas • R$ 1.500,00</p>
                  </div>
                </div>
              </div>

              <DialogFooter>
                <DialogClose asChild>
                  <Button type="button" variant="outline">Cancelar</Button>
                </DialogClose>
                <Button type="submit" data-testid="category-submit-button">
                  {editingCategory ? "Salvar" : "Criar"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Categories Grid */}
      {categories.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Tag size={48} className="mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Nenhuma categoria cadastrada</p>
            <Button 
              variant="link" 
              onClick={() => setDialogOpen(true)}
              className="mt-2"
            >
              Criar primeira categoria
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {categories.map((category, idx) => (
            <Card 
              key={category.id} 
              className="card-hover stagger-item overflow-hidden"
              data-testid={`category-card-${category.id}`}
            >
              <div 
                className="h-2"
                style={{ backgroundColor: category.color }}
              />
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${category.color}20` }}
                    >
                      <Tag size={20} style={{ color: category.color }} weight="fill" />
                    </div>
                    <div>
                      <h3 className="font-heading font-semibold">{category.name}</h3>
                      <p className="text-sm text-muted-foreground font-mono">
                        {category.color}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 mt-4 pt-4 border-t">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEdit(category)}
                    className="gap-1.5 flex-1"
                    data-testid={`edit-category-${category.id}`}
                  >
                    <PencilSimple size={16} />
                    Editar
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(category.id)}
                    className="gap-1.5 text-destructive hover:text-destructive flex-1"
                    data-testid={`delete-category-${category.id}`}
                  >
                    <Trash size={16} />
                    Excluir
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
